package dev.helpmycity.`data`.local.database

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Boolean
import kotlin.Int
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class UserDao_Impl(
  __db: RoomDatabase,
) : UserDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfUserEntity: EntityUpsertAdapter<UserEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfUserEntity = EntityUpsertAdapter<UserEntity>(object : EntityInsertAdapter<UserEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `users` (`id`,`display_name`,`email`,`role`,`scope_citywide`,`scope_districts`,`scope_neighborhoods`,`scope_departments`) VALUES (?,?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: UserEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.displayName)
        val _tmpEmail: String? = entity.email
        if (_tmpEmail == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpEmail)
        }
        statement.bindText(4, entity.role)
        val _tmp: Int = if (entity.scopeCitywide) 1 else 0
        statement.bindLong(5, _tmp.toLong())
        statement.bindText(6, entity.scopeDistricts)
        statement.bindText(7, entity.scopeNeighborhoods)
        statement.bindText(8, entity.scopeDepartments)
      }
    }, object : EntityDeleteOrUpdateAdapter<UserEntity>() {
      protected override fun createQuery(): String = "UPDATE `users` SET `id` = ?,`display_name` = ?,`email` = ?,`role` = ?,`scope_citywide` = ?,`scope_districts` = ?,`scope_neighborhoods` = ?,`scope_departments` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: UserEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.displayName)
        val _tmpEmail: String? = entity.email
        if (_tmpEmail == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpEmail)
        }
        statement.bindText(4, entity.role)
        val _tmp: Int = if (entity.scopeCitywide) 1 else 0
        statement.bindLong(5, _tmp.toLong())
        statement.bindText(6, entity.scopeDistricts)
        statement.bindText(7, entity.scopeNeighborhoods)
        statement.bindText(8, entity.scopeDepartments)
        statement.bindText(9, entity.id)
      }
    })
  }

  public override suspend fun upsert(account: UserEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfUserEntity.upsert(_connection, account)
  }

  public override fun observeAll(): Flow<List<UserEntity>> {
    val _sql: String = "SELECT * FROM users ORDER BY display_name ASC"
    return createFlow(__db, false, arrayOf("users")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfDisplayName: Int = getColumnIndexOrThrow(_stmt, "display_name")
        val _columnIndexOfEmail: Int = getColumnIndexOrThrow(_stmt, "email")
        val _columnIndexOfRole: Int = getColumnIndexOrThrow(_stmt, "role")
        val _columnIndexOfScopeCitywide: Int = getColumnIndexOrThrow(_stmt, "scope_citywide")
        val _columnIndexOfScopeDistricts: Int = getColumnIndexOrThrow(_stmt, "scope_districts")
        val _columnIndexOfScopeNeighborhoods: Int = getColumnIndexOrThrow(_stmt, "scope_neighborhoods")
        val _columnIndexOfScopeDepartments: Int = getColumnIndexOrThrow(_stmt, "scope_departments")
        val _result: MutableList<UserEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: UserEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpDisplayName: String
          _tmpDisplayName = _stmt.getText(_columnIndexOfDisplayName)
          val _tmpEmail: String?
          if (_stmt.isNull(_columnIndexOfEmail)) {
            _tmpEmail = null
          } else {
            _tmpEmail = _stmt.getText(_columnIndexOfEmail)
          }
          val _tmpRole: String
          _tmpRole = _stmt.getText(_columnIndexOfRole)
          val _tmpScopeCitywide: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfScopeCitywide).toInt()
          _tmpScopeCitywide = _tmp != 0
          val _tmpScopeDistricts: String
          _tmpScopeDistricts = _stmt.getText(_columnIndexOfScopeDistricts)
          val _tmpScopeNeighborhoods: String
          _tmpScopeNeighborhoods = _stmt.getText(_columnIndexOfScopeNeighborhoods)
          val _tmpScopeDepartments: String
          _tmpScopeDepartments = _stmt.getText(_columnIndexOfScopeDepartments)
          _item = UserEntity(_tmpId,_tmpDisplayName,_tmpEmail,_tmpRole,_tmpScopeCitywide,_tmpScopeDistricts,_tmpScopeNeighborhoods,_tmpScopeDepartments)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override fun observeById(id: String): Flow<UserEntity?> {
    val _sql: String = "SELECT * FROM users WHERE id = ?"
    return createFlow(__db, false, arrayOf("users")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfDisplayName: Int = getColumnIndexOrThrow(_stmt, "display_name")
        val _columnIndexOfEmail: Int = getColumnIndexOrThrow(_stmt, "email")
        val _columnIndexOfRole: Int = getColumnIndexOrThrow(_stmt, "role")
        val _columnIndexOfScopeCitywide: Int = getColumnIndexOrThrow(_stmt, "scope_citywide")
        val _columnIndexOfScopeDistricts: Int = getColumnIndexOrThrow(_stmt, "scope_districts")
        val _columnIndexOfScopeNeighborhoods: Int = getColumnIndexOrThrow(_stmt, "scope_neighborhoods")
        val _columnIndexOfScopeDepartments: Int = getColumnIndexOrThrow(_stmt, "scope_departments")
        val _result: UserEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpDisplayName: String
          _tmpDisplayName = _stmt.getText(_columnIndexOfDisplayName)
          val _tmpEmail: String?
          if (_stmt.isNull(_columnIndexOfEmail)) {
            _tmpEmail = null
          } else {
            _tmpEmail = _stmt.getText(_columnIndexOfEmail)
          }
          val _tmpRole: String
          _tmpRole = _stmt.getText(_columnIndexOfRole)
          val _tmpScopeCitywide: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfScopeCitywide).toInt()
          _tmpScopeCitywide = _tmp != 0
          val _tmpScopeDistricts: String
          _tmpScopeDistricts = _stmt.getText(_columnIndexOfScopeDistricts)
          val _tmpScopeNeighborhoods: String
          _tmpScopeNeighborhoods = _stmt.getText(_columnIndexOfScopeNeighborhoods)
          val _tmpScopeDepartments: String
          _tmpScopeDepartments = _stmt.getText(_columnIndexOfScopeDepartments)
          _result = UserEntity(_tmpId,_tmpDisplayName,_tmpEmail,_tmpRole,_tmpScopeCitywide,_tmpScopeDistricts,_tmpScopeNeighborhoods,_tmpScopeDepartments)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getById(id: String): UserEntity? {
    val _sql: String = "SELECT * FROM users WHERE id = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfDisplayName: Int = getColumnIndexOrThrow(_stmt, "display_name")
        val _columnIndexOfEmail: Int = getColumnIndexOrThrow(_stmt, "email")
        val _columnIndexOfRole: Int = getColumnIndexOrThrow(_stmt, "role")
        val _columnIndexOfScopeCitywide: Int = getColumnIndexOrThrow(_stmt, "scope_citywide")
        val _columnIndexOfScopeDistricts: Int = getColumnIndexOrThrow(_stmt, "scope_districts")
        val _columnIndexOfScopeNeighborhoods: Int = getColumnIndexOrThrow(_stmt, "scope_neighborhoods")
        val _columnIndexOfScopeDepartments: Int = getColumnIndexOrThrow(_stmt, "scope_departments")
        val _result: UserEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpDisplayName: String
          _tmpDisplayName = _stmt.getText(_columnIndexOfDisplayName)
          val _tmpEmail: String?
          if (_stmt.isNull(_columnIndexOfEmail)) {
            _tmpEmail = null
          } else {
            _tmpEmail = _stmt.getText(_columnIndexOfEmail)
          }
          val _tmpRole: String
          _tmpRole = _stmt.getText(_columnIndexOfRole)
          val _tmpScopeCitywide: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfScopeCitywide).toInt()
          _tmpScopeCitywide = _tmp != 0
          val _tmpScopeDistricts: String
          _tmpScopeDistricts = _stmt.getText(_columnIndexOfScopeDistricts)
          val _tmpScopeNeighborhoods: String
          _tmpScopeNeighborhoods = _stmt.getText(_columnIndexOfScopeNeighborhoods)
          val _tmpScopeDepartments: String
          _tmpScopeDepartments = _stmt.getText(_columnIndexOfScopeDepartments)
          _result = UserEntity(_tmpId,_tmpDisplayName,_tmpEmail,_tmpRole,_tmpScopeCitywide,_tmpScopeDistricts,_tmpScopeNeighborhoods,_tmpScopeDepartments)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getByEmail(email: String): UserEntity? {
    val _sql: String = "SELECT * FROM users WHERE LOWER(email) = LOWER(?)"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, email)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfDisplayName: Int = getColumnIndexOrThrow(_stmt, "display_name")
        val _columnIndexOfEmail: Int = getColumnIndexOrThrow(_stmt, "email")
        val _columnIndexOfRole: Int = getColumnIndexOrThrow(_stmt, "role")
        val _columnIndexOfScopeCitywide: Int = getColumnIndexOrThrow(_stmt, "scope_citywide")
        val _columnIndexOfScopeDistricts: Int = getColumnIndexOrThrow(_stmt, "scope_districts")
        val _columnIndexOfScopeNeighborhoods: Int = getColumnIndexOrThrow(_stmt, "scope_neighborhoods")
        val _columnIndexOfScopeDepartments: Int = getColumnIndexOrThrow(_stmt, "scope_departments")
        val _result: UserEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpDisplayName: String
          _tmpDisplayName = _stmt.getText(_columnIndexOfDisplayName)
          val _tmpEmail: String?
          if (_stmt.isNull(_columnIndexOfEmail)) {
            _tmpEmail = null
          } else {
            _tmpEmail = _stmt.getText(_columnIndexOfEmail)
          }
          val _tmpRole: String
          _tmpRole = _stmt.getText(_columnIndexOfRole)
          val _tmpScopeCitywide: Boolean
          val _tmp: Int
          _tmp = _stmt.getLong(_columnIndexOfScopeCitywide).toInt()
          _tmpScopeCitywide = _tmp != 0
          val _tmpScopeDistricts: String
          _tmpScopeDistricts = _stmt.getText(_columnIndexOfScopeDistricts)
          val _tmpScopeNeighborhoods: String
          _tmpScopeNeighborhoods = _stmt.getText(_columnIndexOfScopeNeighborhoods)
          val _tmpScopeDepartments: String
          _tmpScopeDepartments = _stmt.getText(_columnIndexOfScopeDepartments)
          _result = UserEntity(_tmpId,_tmpDisplayName,_tmpEmail,_tmpRole,_tmpScopeCitywide,_tmpScopeDistricts,_tmpScopeNeighborhoods,_tmpScopeDepartments)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun count(): Int {
    val _sql: String = "SELECT COUNT(*) FROM users"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
